// client/src/components/secure/pm/PMKit.js
//
// Shared vocabulary for the property management screens.
//
// These pages are scanned and operated, not read top to bottom, so the craft here
// is information design rather than typography: the summary comes before the
// detail, and anything needing attention encodes its state in FORM as well as in
// number — a stripe, a pill, a colour — so it reads at a glance without anyone
// having to compare figures.
//
// Semantic colour (overdue / due soon / met) is kept separate from the module's
// own accent, so "this is a property management screen" and "this is on fire" are
// never the same signal.

import React, { useMemo, useState } from 'react';
import { applyList, distinctValues, toCsv } from '../../../utils/listTools';
import SharedPageHeader from '../../shared/PageHeader';

export const money = (v, dash = '—') =>
    v === null || v === undefined || v === '' ? dash
        : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const fmtDate = (d) =>
    d ? new Date(String(d).slice(0, 10) + 'T00:00:00')
        .toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

export const titleCase = (s) =>
    String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ---------------------------------------------------------------- shell
// The portal-wide header (components/shared/PageHeader), so PM and CRM screens match the rest.
export const PageHeader = ({ title, subtitle, children, secondary }) => (
    <SharedPageHeader title={title} description={subtitle} actions={children} secondary={secondary} />
);

export const Panel = ({ title, subtitle, actions, children, className = '' }) => (
    <section className={`bg-white border border-slate-200 rounded-lg shadow-sm ${className}`}>
        {(title || actions) && (
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100">
                <div>
                    {title && <h2 className="font-semibold text-slate-800 text-sm">{title}</h2>}
                    {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
                {actions && <div className="flex gap-2">{actions}</div>}
            </div>
        )}
        <div className="p-5">{children}</div>
    </section>
);

export const Tabs = ({ tabs, active, onChange }) => (
    <div className="border-b border-slate-200 mb-5 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
            {tabs.map((t) => (
                <button
                    key={t.key}
                    onClick={() => onChange(t.key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                        active === t.key
                            ? 'border-slate-700 text-indigo-700'
                            : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                    }`}
                >
                    {t.label}
                    {t.count !== undefined && (
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                            active === t.key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
                        }`}>{t.count}</span>
                    )}
                </button>
            ))}
        </nav>
    </div>
);

// ---------------------------------------------------------------- signals
// A stat tile earns its place by being scannable. The label sits above the figure
// because the eye lands on the number first and needs the label already read.
export const Stat = ({ label, value, sub, tone = 'default', onClick }) => {
    const tones = {
        default: 'border-slate-200',
        good: 'border-emerald-300',
        warn: 'border-amber-300',
        bad: 'border-rose-300',
    };
    const valueTone = {
        default: 'text-slate-800', good: 'text-emerald-700',
        warn: 'text-amber-700', bad: 'text-rose-700',
    };
    return (
        <div
            onClick={onClick}
            className={`bg-white border-l-4 ${tones[tone]} border-y border-r border-y-slate-200 border-r-slate-200
                        rounded-lg px-4 py-3 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
        >
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
            <div className={`text-2xl font-bold tabular-nums mt-1 ${valueTone[tone]}`}>{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
};

export const Pill = ({ children, tone = 'slate' }) => {
    const tones = {
        slate: 'bg-slate-100 text-slate-600',
        indigo: 'bg-indigo-50 text-indigo-700',
        good: 'bg-emerald-50 text-emerald-700',
        warn: 'bg-amber-50 text-amber-700',
        bad: 'bg-rose-50 text-rose-700',
        violet: 'bg-violet-50 text-violet-700',
    };
    return (
        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${tones[tone]}`}>
            {children}
        </span>
    );
};

// The rule stack, shown as what it is: layers, each keeping its own colour so the
// same jurisdiction reads the same way on every screen.
const LAYER_TONE = { federal: 'violet', state: 'indigo', county: 'warn', municipal: 'bad' };
export const LayerChips = ({ layers = [] }) => (
    <div className="flex flex-wrap gap-1">
        {layers.length === 0 && <span className="text-xs text-slate-400">no layers resolved</span>}
        {layers.map((l) => (
            <span key={l.jurisdiction_id || l.short_code} title={l.citation || l.name}>
                <Pill tone={LAYER_TONE[l.layer] || 'slate'}>{l.short_code}</Pill>
            </span>
        ))}
    </div>
);

export const statusTone = (s) => ({
    managed: 'good', active: 'good', met: 'good', approved: 'good', balanced: 'good',
    onboarding: 'warn', draft: 'warn', screening: 'warn', pending_signature: 'warn',
    open: 'warn', in_progress: 'warn', scheduled: 'warn', notice: 'warn', turn: 'warn',
    missed: 'bad', declined: 'bad', discrepancy: 'bad', terminated: 'bad', former: 'bad',
    occupied: 'indigo', vacant: 'slate', ended: 'slate', expired: 'slate',
}[s] || 'slate');

// ---------------------------------------------------------------- data
// Every PM list can be searched, sorted by clicking a header, and downloaded as CSV of
// exactly the rows shown (owner's rule; shared logic in utils/listTools). The order the
// server sends ("what is on fire first") is kept until someone clicks a header.
// filters: optional [{ key, label, options? }] drop-downs, as on the shared DataList.
export const Table = ({ columns, rows, empty = 'Nothing yet.', onRowClick, rowTone, csvName = 'list', toolbar = true, filters: filterDefs = [] }) => {
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState(null);
    const [filters, setFilters] = useState({});
    const shown = useMemo(() => applyList(rows || [], columns, { query, filters, sort }), [rows, columns, query, filters, sort]);
    const clickSort = (c) => {
        if (c.sortable === false) return;
        setSort((s) => (s && s.key === c.key ? (s.dir === 'asc' ? { key: c.key, dir: 'desc' } : null) : { key: c.key, dir: 'asc' }));
    };
    const csv = () => {
        const blob = new Blob([`\ufeff${toCsv(shown, columns)}`], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${csvName}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    return (
        <div>
            {toolbar && (rows || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" aria-label="Search this list"
                        className="flex-1 min-w-[10rem] p-1.5 border border-slate-300 rounded text-sm" />
                    {filterDefs.map((f) => (
                        <select key={f.key} aria-label={f.label} value={filters[f.key] || ''} onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
                            className="p-1.5 border border-slate-300 rounded text-sm bg-white">
                            <option value="">{f.label}: all</option>
                            {(f.options || distinctValues(rows || [], columns.find((c) => c.key === f.key) || { key: f.key })).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    ))}
                    <span className="text-xs text-slate-400">{shown.length} of {rows.length}</span>
                    <button type="button" onClick={csv} className="px-2 py-1 text-xs border border-slate-300 rounded bg-white hover:bg-slate-50">CSV</button>
                </div>
            )}
            <div className="overflow-x-auto -mx-5 px-5">
                <table className="w-full text-sm min-w-max">
                    <thead>
                        <tr className="border-b border-slate-200">
                            {columns.map((c) => (
                                <th key={c.key} onClick={() => clickSort(c)}
                                    className={`text-left py-2 pr-4 text-[10px] uppercase tracking-wider font-bold
                                                text-slate-400 whitespace-nowrap ${c.align === 'right' ? 'text-right pr-0 pl-4' : ''} ${c.sortable === false ? '' : 'cursor-pointer select-none hover:text-slate-700'}`}>
                                    {c.label}{sort && sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.length === 0 && (
                            <tr><td colSpan={columns.length} className="py-8 text-center text-slate-400 text-sm">{(rows || []).length ? 'No rows match.' : empty}</td></tr>
                        )}
                        {shown.map((r, i) => {
                            const tone = rowTone ? rowTone(r) : null;
                            return (
                                <tr key={r._key || i}
                                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                                    className={`border-b border-slate-50 last:border-0
                                        ${onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''}
                                        ${tone === 'bad' ? 'bg-rose-50/40' : tone === 'warn' ? 'bg-amber-50/40' : ''}`}>
                                    {columns.map((c) => (
                                        <td key={c.key}
                                            className={`py-2.5 pr-4 align-top ${c.align === 'right' ? 'text-right pr-0 pl-4 tabular-nums' : ''}
                                                        ${c.nowrap ? 'whitespace-nowrap' : ''}`}>
                                            {c.render ? c.render(r) : (r[c.key] ?? '—')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const Field = ({ label, value, hint }) => (
    <div className="py-2 border-b border-slate-100 last:border-0">
        <dt className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</dt>
        <dd className="text-slate-800 text-sm mt-0.5">
            {value === null || value === undefined || value === ''
                ? <span className="text-slate-400">not recorded</span> : value}
        </dd>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
    </div>
);

// ---------------------------------------------------------------- forms
export const Modal = ({ title, subtitle, onClose, children, wide }) => (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 sm:p-6 overflow-y-auto">
        <div className={`bg-white rounded-lg shadow-xl w-full my-8 ${wide ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <div className="flex justify-between items-start gap-4 px-6 py-4 border-b border-slate-100">
                <div>
                    <h3 className="font-semibold text-slate-800">{title}</h3>
                    {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
                <button onClick={onClose} aria-label="Close"
                    className="text-slate-400 hover:text-slate-700 text-2xl leading-none -mt-1">&times;</button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);

export const Input = ({ label, hint, className = '', ...props }) => (
    <label className={`block mb-3 ${className}`}>
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <input {...props}
            className="mt-1 w-full p-2 border border-slate-300 rounded text-sm
                       focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
        {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
    </label>
);

export const Select = ({ label, hint, options = [], className = '', ...props }) => (
    <label className={`block mb-3 ${className}`}>
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <select {...props}
            className="mt-1 w-full p-2 border border-slate-300 rounded text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
            {options.map((o) => (
                <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? titleCase(o)}</option>
            ))}
        </select>
        {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
    </label>
);

export const Textarea = ({ label, ...props }) => (
    <label className="block mb-3">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <textarea {...props} rows={props.rows || 3}
            className="mt-1 w-full p-2 border border-slate-300 rounded text-sm
                       focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
    </label>
);

export const Button = ({ variant = 'primary', className = '', ...props }) => {
    const v = {
        primary: 'bg-slate-800 text-white hover:bg-slate-900 disabled:bg-indigo-300',
        ghost: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
        danger: 'bg-rose-600 text-white hover:bg-rose-700',
        subtle: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
    }[variant];
    return <button {...props}
        className={`px-3 py-2 rounded text-sm font-medium transition-colors disabled:cursor-not-allowed ${v} ${className}`} />;
};

// ---------------------------------------------------------------- messages
// A refusal from this module is usually a legal one, and it usually arrives as a
// list. Rendering it as a bare toast throws away the part that tells the user what
// to change, so problems[] gets its own treatment.
export const Notice = ({ tone = 'bad', title, message, problems, onDismiss }) => {
    if (!title && !message && !(problems || []).length) return null;
    const tones = {
        bad: 'bg-rose-50 border-rose-200 text-rose-900',
        warn: 'bg-amber-50 border-amber-200 text-amber-900',
        good: 'bg-emerald-50 border-emerald-200 text-emerald-900',
        info: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    };
    return (
        <div className={`border rounded-lg px-4 py-3 mb-4 text-sm ${tones[tone]}`}>
            <div className="flex justify-between items-start gap-4">
                <div>
                    {title && <p className="font-semibold">{title}</p>}
                    {message && <p className={title ? 'mt-1' : ''}>{message}</p>}
                    {(problems || []).length > 0 && (
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                            {problems.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                    )}
                </div>
                {onDismiss && (
                    <button onClick={onDismiss} aria-label="Dismiss"
                        className="opacity-60 hover:opacity-100 text-lg leading-none">&times;</button>
                )}
            </div>
        </div>
    );
};

export const Empty = ({ title, message, action }) => (
    <div className="text-center py-12">
        <p className="text-slate-700 font-medium">{title}</p>
        {message && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{message}</p>}
        {action && <div className="mt-4">{action}</div>}
    </div>
);

export const Loading = () => (
    <div className="py-16 text-center text-slate-400 text-sm">Loading…</div>
);
